import { Injectable, PLATFORM_ID, REQUEST, RESPONSE_INIT, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { SupabaseClient } from '@supabase/supabase-js';
import { createBrowserClient, createServerClient } from '@supabase/ssr';

import { environment } from '../../../environments/environment';
import { ServicesApi } from './modules/services';
import { ProjectsApi } from './modules/projects';
import { WorkspacesApi } from './modules/workspaces';
import { ResourcesApi } from './modules/resources';
import { UserApi } from './modules/user';
import { AccountingApi } from './modules/accounting';
import { ApiKeysApi } from './modules/apikeys';
import { SshKeysApi } from './modules/sshkeys';
import { JobsApi } from './modules/jobs';
import { ContactApi } from './modules/contact';
import { NotificationsApi } from './modules/notifications';
import { AnnouncementsApi } from './modules/announcements';
import { AuthApi } from './modules/auth';
import { StorageApi } from './modules/storage';

function parseCookies(cookieHeader: string): { name: string; value: string }[] {
  return cookieHeader.split(';').reduce<{ name: string; value: string }[]>((acc, pair) => {
    const idx = pair.indexOf('=');
    if (idx > 0) {
      acc.push({ name: pair.slice(0, idx).trim(), value: decodeURIComponent(pair.slice(idx + 1).trim()) });
    }
    return acc;
  }, []);
}

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private request = inject(REQUEST, { optional: true });
  private responseInit = inject(RESPONSE_INIT, { optional: true });

  private _supabase!: SupabaseClient;

  constructor() {
    // Runs synchronously before workspace.service.ts's hasAuthToken() checks localStorage.
    // If no cookie-based session exists, purge stale localStorage sb-* tokens so
    // hasAuthToken() doesn't falsely trigger workspace loading with an expired session.
    if (isPlatformBrowser(this.platformId)) {
      const hasCookieSession = document.cookie.split(';').some(c => c.trim().startsWith('sb-'));
      if (!hasCookieSession) {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key?.startsWith('sb-')) localStorage.removeItem(key);
        }
      }
    }
  }

  readonly user = new UserApi(this.http);
  readonly accounting = new AccountingApi(this.http);
  readonly projects = new ProjectsApi(this.http);
  readonly services = new ServicesApi(this.http);
  readonly workspaces = new WorkspacesApi(this.http);
  readonly resources = new ResourcesApi(this.http);
  readonly apikeys = new ApiKeysApi(this.http);
  readonly sshkeys = new SshKeysApi(this.http);
  readonly jobs = new JobsApi(this.http);
  readonly contact = new ContactApi(this.http);
  readonly notifications = new NotificationsApi(this.http);
  readonly announcements = new AnnouncementsApi(this.http);
  readonly auth = new AuthApi(this.http);
  readonly storage = new StorageApi(this.http);

  get supabase(): SupabaseClient {
    if (this._supabase) return this._supabase;

    if (isPlatformBrowser(this.platformId)) {
      // createBrowserClient stores the session in cookies automatically.
      // The lock bypass prevents navigator.locks deadlocks in Angular's zone.
      this._supabase = createBrowserClient(environment.supaApiUrl, environment.supaApiKey, {
        auth: {
          lock: async <R>(_name: string, _timeout: number, fn: () => R | Promise<R>): Promise<R> => await fn(),
        },
      });

      // Sync session to localStorage so hasAuthToken() in workspace.service.ts works
      // on the bootstrap path, where no session object is at hand. Registered before
      // WorkspaceService's own listener, so the key is written first.
      const storageKey = `sb-${new URL(environment.supaApiUrl).hostname.split('.')[0]}-auth-token`;

      this._supabase.auth.onAuthStateChange((_event, session) => {
        if (session) {
          localStorage.setItem(storageKey, '1');
        } else {
          localStorage.removeItem(storageKey);
        }
      });
    } else {
      const req = this.request;
      const resInit = this.responseInit;
      this._supabase = createServerClient(environment.supaApiUrl, environment.supaApiKey, {
        cookies: {
          getAll: () => parseCookies(req?.headers.get('cookie') ?? ''),
          setAll: (cookiesToSet) => {
            if (!resInit) return;
            resInit.headers = cookiesToSet.map(({ name, value, options }) => {
              let cookie = `${name}=${value}; Path=${options?.path ?? '/'}`;
              if (options?.maxAge) cookie += `; Max-Age=${options.maxAge}`;
              if (options?.domain) cookie += `; Domain=${options.domain}`;
              if (options?.httpOnly) cookie += '; HttpOnly';
              if (options?.secure) cookie += '; Secure';
              if (options?.sameSite) cookie += `; SameSite=${options.sameSite}`;
              return ['Set-Cookie', cookie] as [string, string];
            });
          },
        },
      });
    }

    return this._supabase;
  }
}
